import type { AppServices } from '../app';
import { CULTURES, CultureId, isSharedCulture } from '../data/cultures';
import { simbadById } from '../live/simbad';
import type { Place } from '../astro/frame';
import { calendarFocus, calendarPlace } from '../live/skycalendar';
import { Selection, fovDeg, panel, place, previewPlace, selection, setSetting, setSimTime, settings, simNow, timeLive, viewMode } from '../state';

// Links that open Ahillah on exactly the same sky: place, moment, direction, zoom and selection.
// The place is rounded to about 1 km; nothing else about the sender is included.

function selToParam(s: AppServices, sel: Selection | null): string | null {
  if (!sel) return null;
  switch (sel.kind) {
    case 'body': return `body:${sel.id}`;
    case 'star': {
      const hip = s.engine.stars.hip[sel.index];
      return hip ? `hip:${hip}` : null;
    }
    case 'dso': return `dso:${s.engine.dsos[sel.index].id}`;
    case 'constellation': return `con:${s.engine.cons.constellations[sel.index].id}`;
    case 'satellite': return `sat:${sel.id}`;
    case 'shower': return `shower:${sel.code}`;
    case 'small': return `small:${sel.name}`;
    case 'arabic': return `arabic:${sel.index}`;
    case 'manzil': return `manzil:${sel.index}`;
    // A figure belongs to its sky culture, which the link switches to.
    case 'figure': return `fig:${settings.peek().skyCulture}:${sel.index}`;
    case 'simbad': return `simbad:${sel.id}`;
    default: return null;
  }
}

const validIndex = (id: string, length: number) => {
  const n = Number(id);
  return Number.isInteger(n) && n >= 0 && n < length ? n : null;
};

export function paramToSel(s: AppServices, v: string | null): Selection | null {
  if (!v) return null;
  const i = v.indexOf(':');
  if (i < 1) return null;
  const kind = v.slice(0, i), id = v.slice(i + 1);
  switch (kind) {
    case 'body': return ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune'].includes(id) ? { kind: 'body', id } : null;
    case 'hip': {
      const k = s.engine.stars.byHip.get(Number(id));
      return k === undefined ? null : { kind: 'star', index: k };
    }
    case 'dso': {
      const k = s.engine.dsos.findIndex((d) => d.id === id);
      return k < 0 ? null : { kind: 'dso', index: k };
    }
    case 'con': {
      const k = s.engine.cons.constellations.findIndex((c) => c.id === id);
      return k < 0 ? null : { kind: 'constellation', index: k };
    }
    // These lists may still be loading, so only the form is checked; the cards cope with unknown ids.
    case 'sat': return /^\d{1,9}$/.test(id) ? { kind: 'satellite', id: Number(id) } : null;
    case 'shower': return /^[A-Z]{3}$/.test(id) ? { kind: 'shower', code: id } : null;
    case 'small': return id.length > 0 && id.length <= 80 ? { kind: 'small', name: id } : null;
    case 'arabic': {
      const n = validIndex(id, 152);
      return n === null ? null : { kind: 'arabic', index: n };
    }
    case 'manzil': {
      const n = validIndex(id, 28);
      return n === null ? null : { kind: 'manzil', index: n };
    }
    default: return null;
  }
}

/** A link to the current view. */
export function viewUrl(s: AppServices): string {
  const p = place.peek();
  const q = new URLSearchParams();
  if (!timeLive.peek()) q.set('at', simNow().toISOString().slice(0, 16) + 'Z');
  q.set('lat', p.lat.toFixed(2));
  q.set('lon', p.lon.toFixed(2));
  q.set('place', p.name);
  if (p.timeZone) q.set('tz', p.timeZone);
  if (viewMode.peek() === 'manual') {
    const c = s.engine.viewCentre();
    q.set('az', c.az.toFixed(1));
    q.set('alt', c.alt.toFixed(1));
    q.set('fov', fovDeg.peek().toFixed(fovDeg.peek() < 5 ? 2 : 0));
  }
  const sel = selToParam(s, selection.peek());
  if (sel) q.set('sel', sel);
  return `${location.origin}${import.meta.env.BASE_URL}?${q.toString()}`;
}

/** The place in an address (lat, lon, place, tz), checked; null if there is none. */
function placeFrom(q: URLSearchParams): Place | null {
  const lat = Number(q.get('lat')), lon = Number(q.get('lon'));
  if (!q.has('lat') || !q.has('lon') || !Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  const name = (q.get('place') ?? 'Shared place').slice(0, 60);
  // Only a real time zone name: anything else would break every clock in the app.
  let timeZone: string | undefined = q.get('tz') ?? undefined;
  try {
    if (timeZone) new Intl.DateTimeFormat('en-GB', { timeZone });
  } catch {
    timeZone = undefined;
  }
  return { lat, lon, height: 20, name, timeZone };
}

/** This visit opened on a shared direction or zoom, which pointing leaves on screen until Point is tapped. Kept here
 * because the address is tidied away below, before pointing would start. */
let sharedView = false;
export const openedOnSharedView = () => sharedView;

/** Open the view described by the address, if any (from a shared link). */
export function applyUrlState(s: AppServices) {
  const q = new URLSearchParams(location.search);
  // The owner's own page, by its address alone.
  if (q.has('owner')) {
    panel.value = 'owner';
    history.replaceState(null, '', location.pathname + (q.has('silent') ? '?silent' : ''));
    return;
  }
  // Opened from an alert or a home-screen shortcut: straight to that part of the app.
  const open = q.get('panel');
  if (open && (['tonight', 'go', 'learn', 'quiz', 'search', 'calendar', 'gallery', 'more'] as const).includes(open as never)) {
    // From a sky event's reminder: open the calendar at that event, for the place it was worked out for.
    const ev = q.get('ev');
    if (open === 'calendar' && ev && /^[\w:.-]{1,60}$/.test(ev)) {
      calendarFocus.value = ev;
      const p = placeFrom(q);
      if (p) calendarPlace.value = p;
    }
    panel.value = open as 'tonight';
    history.replaceState(null, '', location.pathname);
    return;
  }
  if (!q.has('lat') && !q.has('at') && !q.has('sel')) return;
  const shared = placeFrom(q);
  if (shared) {
    const home = place.peek();
    if (Math.abs(home.lat - shared.lat) > 0.05 || Math.abs(home.lon - shared.lon) > 0.05) previewPlace(shared, shared.name, 'link');
  }
  const at = q.get('at');
  const atMs = at ? Date.parse(at) : NaN;
  // The same range as the special-day picker.
  if (Number.isFinite(atMs) && atMs >= Date.UTC(1600, 0, 1) && atMs <= Date.UTC(2200, 11, 31)) setSimTime(new Date(atMs), 0);
  const az = Number(q.get('az')), alt = Number(q.get('alt')), fov = Number(q.get('fov'));
  if (q.has('az') && Number.isFinite(az) && Number.isFinite(alt)) {
    s.engine.viewAz = az;
    s.engine.viewAlt = Math.max(-89, Math.min(89, alt));
    sharedView = true;
  }
  if (q.has('fov') && Number.isFinite(fov)) {
    fovDeg.value = Math.max(0.05, Math.min(150, fov));
    sharedView = true;
  }
  const sel = paramToSel(s, q.get('sel'));
  // Select once the sky has been drawn (positions exist); Arabic-sky selections also need their data.
  const whenReady = (fn: () => void) => {
    const go = () => (s.engine.bodies.length ? fn() : setTimeout(go, 100));
    go();
  };
  if (sel && (sel.kind === 'arabic' || sel.kind === 'manzil')) void s.engine.ensureArabic().then(() => whenReady(() => (selection.value = sel)));
  else if (sel) whenReady(() => (selection.value = sel));
  // A sky-culture figure: switch to its culture, then select it once loaded.
  const fig = /^fig:([a-z_]{2,30}):(\d{1,4})$/.exec(q.get('sel') ?? '');
  const culture = fig && CULTURES.find((c) => c.id === fig[1] && isSharedCulture(c.id))?.id;
  if (fig && culture) {
    setSetting('skyCulture', culture as CultureId);
    void s.engine.ensureCulture(culture).then(() =>
      whenReady(() => {
        const n = Number(fig[2]);
        if (s.engine.culture?.data.id === culture && n < s.engine.culture.data.figures.length) selection.value = { kind: 'figure', index: n };
      }),
    );
  }
  // Something found in SIMBAD: look it up again by name.
  const sim = /^simbad:(.{1,80})$/.exec(q.get('sel') ?? '');
  if (sim) void simbadById(sim[1]).then((o) => o && whenReady(() => (selection.value = o))).catch(() => {});
  // Tidy the address so a reload starts normally.
  history.replaceState(null, '', location.pathname + (q.has('silent') ? '?silent' : ''));
}
